import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';

import { ConfigService } from '@finder/core/config.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { LazyLoaderService } from '@finder/shared/services/lazy-loader/lazy-loader-service';
import { DebugService } from '@finder/features/debug/services/debug-service/debug.service';
import { BehaviorSubject, filter, from, fromEvent, take } from 'rxjs';

const CLASS_NAME = 'FINDER-MEDIA-GALLERY: ';

@Component({
    selector: 'finder-media-gallery',
    templateUrl: './media-gallery.component.html',
    styleUrls: ['./media-gallery.component.scss'],
    standalone: true,
})
export class MediaGalleryComponent implements OnInit, AfterViewInit {
    @Input() config;
    @Input() version = '3.0.0';
    @Input() theme = 'snowball-no-thumbs';
    @Input() production = false;
    @Output() mediaEngineIsReady: EventEmitter<any> = new EventEmitter();

    blockNumber = 0;

    private mediaEngine;
    private isMediaEngineReady = new BehaviorSubject<any>(null);

    constructor(
        private logger: LoggerService,
        private windowRef: WindowRef,
        private configService: ConfigService,
        private lazyLoader: LazyLoaderService,
        private debugService: DebugService,
        private elementRef: ElementRef,
    ) { }

    ngOnInit() {
        if (!this.config) {
            this.logger.error(`${CLASS_NAME} no config present`);

            return;
        }

        this.blockNumber = this.getRandomInt();

        this.loadRequiredAssets();
    }

    ngAfterViewInit(): void {
        this.loadMediaEngine();
    }

    getElementReference() {
        return this.elementRef.nativeElement;
    }

    private loadMediaEngine() {
        /**
         * I'm using a BehaviorSubject because I need to wait for the view
         * to finish loading. Once it has we can check the BehaviorSubject to see
         * if media engine has finished loading. There's a possible race condition where
         * the view could be ready before ME so we want to wait.
         */
        this.isMediaEngineReady
            .asObservable()
            .pipe(
                filter(isReady => isReady),
                take(1)
            ).subscribe(() => {
                this.mediaEngine = new this.windowRef.nativeWindow.ME3();
                this.mediaEngine.nativeElement = this.getElementReference();

                // The `callbacks` object doesn't always exist. Check for it.
                this.config.callbacks = this.config.callbacks || {};

                /**
                 * Create a callback we can use later to get a fully
                 * initialized instance of media engine
                 */
                this.config.callbacks.getMediaEngine = () => this.mediaEngine;

                // Emit the media engine so it can be controlled externally
                this.mediaEngine.dispatchReady = () => {
                    this.mediaEngineIsReady.emit(this.mediaEngine);
                };

                /**
                 * Wait till the media engine is ready and emit it to the parent component.
                 */
                if (this.config.callbacks.ready) {
                    /**
                     * If a "ready" callback exists wait until the 'me-ready' event bubbles
                     * and then trigger the emit.
                     */
                    fromEvent(this.getElementReference(), 'me-ready')
                        .pipe(take(1))
                        .subscribe(() => this.mediaEngine.dispatchReady());
                } else {
                    // If there is no "ready" callback attach to it so we can emit to the component
                    this.config.callbacks.ready = this.mediaEngine.dispatchReady;
                }

                // Start up media engine
                this.mediaEngine.init(`.me${this.blockNumber}`, this.config);

                // Track all the ME configs for debugging purposes
                /* istanbul ignore if */
                if (this.debugService.isDebugEnabled()) {
                    this.getElementReference().setAttribute('data-me-config', this.blockNumber);
                    this.windowRef.nativeWindow.meConfigs = this.windowRef.nativeWindow.meConfigs || [];
                    this.windowRef.nativeWindow.meConfigs.push({
                        id: this.blockNumber,
                        config: this.mediaEngine,
                    });
                }
            });
    }

    private loadRequiredAssets() {
        const baseUrl = `${this.configService.getCdnUrl()}/media/media-engine/${this.version}`;

        // Load the base file and our theme
        this.lazyLoader.lazyLoadCssResource(`${baseUrl}/css/media-engine.css`, false);
        this.lazyLoader.lazyLoadCssResource(`${baseUrl}/css/media-engine.theme.${this.theme}.css`, false);

        // Load the js file. When done init the gallery
        this.lazyLoader.lazyLoadScriptResource(`${baseUrl}/js/media-engine.js`, false)
            .subscribe({
                complete: () => {
                    this.isMediaEngineReady.next(true);
                }
            });
    }

    private getRandomInt(max = 1000) {
        return Math.floor(Math.random() * max);
    }
}
